import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
} from '@fortawesome/free-solid-svg-icons';

import { useModalStore } from '@/stores';
import { Button } from '@/components/ui';
import type { ProviderContact } from '@/types/provider';
import styles from './SimilarProvidersModal.module.css';

export interface SimilarProvider {
  id: string;
  nombre_comercial: string;
  email: string | null;
  telefono: string | null;
  pais: string | null;
  ciudad: string | null;
  distrito: string | null;
  state: string;
  calificacion: number | null;
  contacts?: ProviderContact[];
}

interface SimilarProvidersModalProps {
  providers: SimilarProvider[];
  onSelectProvider: (provider: SimilarProvider) => void;
  handleClose?: () => void;
}

export function SimilarProvidersModal({ providers, onSelectProvider }: SimilarProvidersModalProps) {
  const { closeModal } = useModalStore();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const handleConfirm = () => {
    const selected = providers.find((p) => p.id === selectedId);
    if (selected) {
      onSelectProvider(selected);
      closeModal();
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.iconContainer}>
        <FontAwesomeIcon icon={faExclamationTriangle} className={styles.icon} />
      </div>

      <h3 className={styles.title}>Similar providers found</h3>

      <p className={styles.subtitle}>
        The uploaded document matches {providers.length} existing provider
        {providers.length > 1 ? 's' : ''}. Select one to edit it instead, or continue creating a new
        one.
      </p>

      <div className={styles.providerList}>
        {providers.map((p) => (
          <div
            key={p.id}
            className={`${styles.providerCard} ${selectedId === p.id ? styles.providerCardSelected : ''}`}
            onClick={() => setSelectedId(p.id)}
          >
            <div
              className={`${styles.radioCircle} ${selectedId === p.id ? styles.radioCircleSelected : ''}`}
            >
              {selectedId === p.id && <div className={styles.radioCircleInner} />}
            </div>
            <div className={styles.providerInfo}>
              <div className={styles.providerName}>{p.nombre_comercial}</div>
              <div className={styles.providerMeta}>
                {(p.contacts?.find((c) => c.es_principal)?.email || p.contacts?.[0]?.email || p.email) && (
                  <span className={styles.providerMetaItem}>
                    <FontAwesomeIcon icon={faEnvelope} className={styles.providerMetaIcon} />
                    {p.contacts?.find((c) => c.es_principal)?.email || p.contacts?.[0]?.email || p.email}
                  </span>
                )}
                {(p.contacts?.find((c) => c.es_principal)?.telefono || p.contacts?.[0]?.telefono || p.telefono) && (
                  <span className={styles.providerMetaItem}>
                    <FontAwesomeIcon icon={faPhone} className={styles.providerMetaIcon} />
                    {p.contacts?.find((c) => c.es_principal)?.telefono || p.contacts?.[0]?.telefono || p.telefono}
                  </span>
                )}
                {(p.ciudad || p.pais) && (
                  <span className={styles.providerMetaItem}>
                    <FontAwesomeIcon icon={faMapMarkerAlt} className={styles.providerMetaIcon} />
                    {[p.ciudad, p.pais].filter(Boolean).join(', ')}
                  </span>
                )}
              </div>
            </div>
            <span
              className={`${styles.stateBadge} ${p.state === 'activo' ? styles.stateBadgeActive : styles.stateBadgeDraft}`}
            >
              {p.state === 'activo' ? 'Active' : 'Draft'}
            </span>
          </div>
        ))}
      </div>

      <div className={styles.actions}>
        <Button variant="secondary" onClick={closeModal}>
          Continue as new
        </Button>
        <Button variant="primary" onClick={handleConfirm} disabled={selectedId === null}>
          Edit selected
        </Button>
      </div>
    </div>
  );
}
